/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: photov2 专用导出 Excel 按钮（基于 photosByContentSlice 与 photoFilterSlice）
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 */
import React, { useRef, useMemo, useEffect } from 'react'
import { Button, message, Modal, Select } from 'antd'
import {
  useAppDispatch,
  useAppSelector,
  usePayment,
  useVip
} from '../../../../app/hooks'
import exportAsExcelExample from '../../../../images/export_photo_as_excel_example.png'
import vipIcon from '../../../../images/vip_download_excel.png'
import {
  buildFetchPhotosParamsWithOptions,
  calcPickedMemberNum
} from '../../lib/photo'
import sensors from '../../../../utils/sensors'
import { trackPicsPageClickV2 } from '../../utils/track'
import { exportPhotosAsExcel } from '../../../../actions/photo'
import actionTypes from '../../../../constants/actionTypes'
import CONFIG from '../../../../constants/config'
import createExcelTask, {
  ExcelTaskType
} from '../../../../app/libs/createExcelTask'
import { selectSearchFilter } from '../../../../slices/photoFilterSlice'
import { selectPreciseSearch } from '../../../../slices/photosByPreciseSlice'
import {
  getSelectionConfig,
  normalizeToSet,
  PhotoSelectionSource,
  useActiveSelection,
  buildSuggestedPhotosQueryFilter
} from '../../utils/selectionHelpers'

interface ExportExcelButtonProps {
  dataSource?: PhotoSelectionSource
}

const ExportExcelButton: React.FC<ExportExcelButtonProps> = ({
  dataSource = 'content'
}) => {
  const { Option } = Select
  const dispatch = useAppDispatch()

  const queryFilter = useAppSelector(selectSearchFilter)
  const listIsAscSort = useAppSelector((state) => {
    if (dataSource === 'precise') {
      return state.photosByPrecise.isAscSort
    }
    return state.photosByContent.isAscSort
  })
  const preciseSearch = useAppSelector(selectPreciseSearch)
  const groupID = useAppSelector((state) => state.group.groupId)
  const members = useAppSelector((state) => state.group.members)

  const {
    selectionConfig,
    hasSuggestedSelected,
    activePhotos: photos,
    activePhotosNum: photosNum,
    activeCheckAll: checkAll,
    activeCheckedPhotos: checkedPhotos,
    activeUnCheckedPhotos: unCheckedPhotos,
    suggestedWords,
    suggestedQueryFilter
  } = useActiveSelection(dataSource)

  const payment = usePayment()
  const { isVip } = useVip()
  const formatTypeRef = useRef(0)

  const triggerPhotoWaterMarkExcel = useAppSelector(
    (state) => state.photoWatermark.triggerPhotoWaterMarkExcel
  )

  const disabled = useMemo(() => {
    return checkedPhotos.size === 0 && !checkAll
  }, [checkedPhotos, checkAll])

  // 计算选中的照片数量
  const checkedNum = useMemo(() => {
    if (checkAll) {
      return photosNum - unCheckedPhotos.size
    }
    return checkedPhotos.size
  }, [checkAll, photosNum, checkedPhotos, unCheckedPhotos])

  // 计算排除的照片
  const exclude: string[] = useMemo(() => {
    if (checkAll) {
      return photos
        .filter((photo) => unCheckedPhotos.has(photo.seq))
        .map((photo) => photo.file)
    }
    return []
  }, [checkAll, photos, unCheckedPhotos])

  // 计算包含的照片
  const include: string[] = useMemo(() => {
    if (!checkAll) {
      return photos
        .filter((photo) => checkedPhotos.has(photo.seq))
        .map((photo) => photo.file)
    }
    return []
  }, [checkAll, photos, checkedPhotos])

  // 取消全选
  const cancelCheckAll = () => {
    if (hasSuggestedSelected) {
      dispatch({ type: 'photosByContent/clearSuggestedCheck' })
    } else {
      dispatch(selectionConfig.clearCheckAction())
    }
  }

  const modalEventTrack = (actionType: string) => {
    sensors.track('web_get_action_pop_export_excel', {
      actionType,
      fromPlace: 'watermarkContent'
    })
  }

  const handleExport = (isSinglePaid = false) => {
    if (!queryFilter || !groupID) return

    modalEventTrack('exportExcel')

    let currentQueryFilter = queryFilter
    if (hasSuggestedSelected) {
      currentQueryFilter =
        buildSuggestedPhotosQueryFilter(
          queryFilter,
          suggestedQueryFilter,
          suggestedWords || []
        ) || queryFilter
    }

    const isPreciseSearch = dataSource === 'precise'
    const exportFilter: any = buildFetchPhotosParamsWithOptions({
      queryFilter: currentQueryFilter,
      groupID,
      lastPageCond: '',
      isAscSort: listIsAscSort,
      isPreciseSearch,
      excludeKeywords: isPreciseSearch ? preciseSearch?.notWords : undefined
    })

    if (checkAll) {
      exportFilter.filter.exclude = exclude
    } else {
      exportFilter.filter.include = include
    }
    exportFilter.formatType = formatTypeRef.current

    if (!isVip && !isSinglePaid) {
      payment.open('webAllPhotoExportExcel')
      return Promise.resolve()
    }

    if (checkedNum > CONFIG.maxDownloadLimit) {
      sensors.track(
        'web_show_pop_workgroup_download_delete_photo_limit_exceeded',
        {
          operationType: 'download',
          photoNum: checkedNum,
          groupID
        }
      )
      Modal.info({
        title: `团队会员最多可支持${CONFIG.maxDownloadLimit}张照片的下载`,
        content: (
          <div>
            <p>请您适当的减少照片张数后下载</p>
          </div>
        ),
        okText: '我知道了',
        onOk: cancelCheckAll
      })
      return
    }

    formatTypeRef.current = 0

    return dispatch(exportPhotosAsExcel(exportFilter)).then((result: any) => {
      const { data } = result
      if (data.exportResult !== 1) {
        if (data.exportResult === -202) {
          Modal.warning({
            title: '提示',
            content: '选择时间段内无照片'
          })
          return Promise.resolve()
        } else {
          message.error('导出失败，请联系客服')
        }
        return Promise.reject()
      }
      const exportParams: any = {
        groupID,
        membersNum: calcPickedMemberNum(currentQueryFilter, members),
        exportType: ExcelTaskType.ALL_PHOTO_EXCEL,
        startDate: currentQueryFilter.times?.[0]?.format(CONFIG.timeFormat),
        endDate: currentQueryFilter.times?.[1]?.format(CONFIG.timeFormat),
        finished: data.exportResult,
        fileName: data.fileName,
        url: data.exportURL,
        contentNumber: data.contentNumber
      }
      if (isSinglePaid) {
        exportParams.extra = {
          orderNo: localStorage.getItem('orderNo')
        }
      }
      createExcelTask(exportParams)
      cancelCheckAll()
      if (isSinglePaid) {
        dispatch({
          type: actionTypes.REPLACE_ROUTE,
          payload: '/attendance/export-list'
        })
        return
      }
      window.open('/attendance/export-list?fromPlace=photoAll')
      return Promise.resolve()
    })
  }

  useEffect(() => {
    if (triggerPhotoWaterMarkExcel) {
      handleExport(true)
      dispatch({ type: actionTypes.TRIGGER_PHOTO_WATERMARK_EXCEL, data: false })
    }
  }, [triggerPhotoWaterMarkExcel])

  const handleClick = () => {
    trackPicsPageClickV2({ clickItem: 'exportExcel' })
    modalEventTrack('showPop')
    Modal.confirm({
      title: (
        <span style={{ width: '100%', display: 'block', textAlign: 'center' }}>
          导出后将自动汇总生成表格
        </span>
      ),
      icon: null,
      width: 560,
      centered: true,
      mask: { closable: false },
      content: (
        <div>
          <img style={{ width: '100%' }} src={exportAsExcelExample} alt="" />
          <Select
            defaultValue="0"
            className="select-type"
            style={{
              position: 'absolute',
              right: 32,
              width: 130,
              border: 'none',
              margin: 0
            }}
            onChange={(value) => {
              formatTypeRef.current = Number(value)
            }}
          >
            <Option value="0">支持缩放图片</Option>
            <Option value="1">支持筛选排序</Option>
          </Select>
        </div>
      ),
      okText: (
        <div>
          导出Excel
          <img style={{ width: 20 }} src={vipIcon} alt="" />{' '}
        </div>
      ),
      cancelText: '取消',
      onOk: () => {
        return handleExport()
      },
      onCancel: () => modalEventTrack('clickCancel')
    })
  }

  return (
    <Button
      className="export-excel-btn"
      type="primary"
      ghost
      style={{ marginRight: 10 }}
      disabled={disabled}
      onClick={handleClick}
    >
      导出Excel
    </Button>
  )
}

export default ExportExcelButton
